import { useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Box } from '@mui/system';
import {
  GridCellModes,
  GridCellParams,
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef,
  GridColType,
  GridFilterOperator,
  getGridNumericOperators,
} from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import { Interweave } from 'interweave';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import { ServiceListProps } from './ServiceList.props';
import {
  LogoImageBox,
  ImageWrapper,
  Image,
  SwitchGrid,
  HeaderWrapper,
  GridContainer,
} from './ServiceList.styles';
import {
  ActionPermissions,
  CustomPermission,
  LabelValue,
  ListType,
  PropReportDataType,
  NewReportDataType,
  ReportResponse,
  OverwriteReportDataType,
  ColorProps,
  ScreenSize,
  CustomDensityOption,
} from '../../../@types';
import {
  BulkEditServiceDataType,
  ServiceBillingInfo,
  ServiceFilterMultiSelectType,
  ServiceListDataType,
} from '../../../@types/service.type';
import { FileAttachmentURLType, FileMimeTypes } from '../../../@types/uploaded-files.type';
import { Chip } from '../../../components/atoms/Chip';
import { FormControlLabel } from '../../../components/atoms/FormControlLabel';
import { Link } from '../../../components/atoms/Link';
import { Modal } from '../../../components/atoms/Modal';
import { MultiPasteFilterInput } from '../../../components/atoms/MultiPasteFilterInput/MultiPasteFilterInput';
import { DropdownItem, SelectMenu } from '../../../components/atoms/SelectMenu';
import { Snackbar } from '../../../components/atoms/Snackbar';
import { Switch } from '../../../components/atoms/Switch';
import { Typography, TypographyVariantProps } from '../../../components/atoms/Typography';
import { CustomDataGrid } from '../../../components/layouts/CustomDataGrid';
import { EditField } from '../../../components/molecules/EditField';
import { InvoiceList } from '../../../components/molecules/InvoiceList';
import { ServiceListMobileView } from '../../../components/molecules/ServiceListMobileView';
import { TagCell } from '../../../components/molecules/TagCell';
import { SelectedServiceListDataType } from '../../../components/organisms/BulkServiceEditor';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { COLORS } from '../../../configs/colors';
import { PLATFORM_NAME } from '../../../configs/common';
import { InvoiceType, SavedReportTypeList, GridFeatureModeConstant } from '../../../configs/enums';
import { isUserHasPermission } from '../../../configs/permissions';
import { MOBILE_DATA_GRID_COLUMN } from '../../../configs/ui-constants';
import { UploadedFilesKey } from '../../../configs/uploaded-files';
import {
  columnSelectionItems,
  SERVICE_ATTACHMENT_FILE_URL_DATA,
  SERVICE_BILLING_DATA,
  linkedFieldItems,
} from '../../../constants/service.constant';
import { useGetZones } from '../../../queries/geosocial-query';
import { useGetRegionTypes } from '../../../queries/regionTypes-query';
import {
  useAddNewReport,
  useGetDefaultReportByReportType,
  useGetReportByCode,
  useUpdateReportByCode,
} from '../../../queries/report-query';
import {
  useGetActionTypes,
  useGetBulkServicesByIds,
  useGetProcessTypes,
  useGetServicePaginatedData,
  useUpdateBulkServiceList,
  useGetBillingInfo,
  useUpdateBillingInfo,
  useGetServiceBillingTypes,
  useGetServiceActionProcessTypes,
} from '../../../queries/service-query';
import { useGetServicesStatuses } from '../../../queries/service-status-query';
import { useGetUploadedFilePresignedUrlByKey } from '../../../queries/uploadedfiles-query';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import {
  checkListPagetFilterAvailability,
  formatDate,
  getDateFromTimestamp,
} from '../../../utils/common';
import { downloadFile } from '../../../utils/file-utils';
import { generateReportURL } from '../../../utils/report';
import {
  checkMobileViewColumnVisibility,
  formatServiceAttachmentsData,
  getBillingTypesDropdownData,
  getInvoicesByType,
  getServiceActionProcessTypesForMultiSelectFilters,
  getServiceAttachmentURLData,
  getServiceDefaultReportType,
  getServiceListInitialFilterModel,
  getServicesListFilteredColumns,
  handleServiceListCoulumnPermissions,
} from '../../../utils/services';

export const ServiceList: React.FC<ServiceListProps> = ({ multiSearchQuery }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const apiRef = useGridApiRef();

  const defaultReport = getServiceDefaultReportType(location.pathname);
  const [searchParams] = useSearchParams();
  const reportCode = searchParams.get('savedReportCode');
  const locationId = searchParams.get('locationId');
  const includeChildRecords = searchParams.get('includeChildRecords');
  const isFromStructures = searchParams.get('isFromStructures');
  const importSessionId = searchParams.get('importSessionId');
  const clientCode = searchParams.get('clientCode');
  const serviceTypeName = searchParams.get('serviceTypeName');
  const serviceStatusGroup = searchParams.get('serviceStatusGroup');
  const initialFilterModel = defaultReport
    ? []
    : getServiceListInitialFilterModel(
        importSessionId,
        clientCode,
        serviceTypeName,
        serviceStatusGroup,
        locationId
      );

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [rowCountState, setRowCountState] = useState(0);
  const [services, setServices] = useState<ServiceListDataType[]>([]);
  const [statusesList, setStatusesList] = useState<LabelValue[]>([]);
  const [actionTypesList, setActionTypesList] = useState<LabelValue[]>([]);
  const [processTypesList, setProcessTypesList] = useState<LabelValue[]>([]);
  const [selectedServices, setSelectedServices] = useState<SelectedServiceListDataType[]>([]);
  const [selectedServicesUsingCsv, setSelectedServicesUsingCsv] = useState<
    SelectedServiceListDataType[]
  >([]);
  const [BulkUpdateMessage, setBulkUpdateMessage] = useState<string>('');
  const [permissions, setPermissions] = useState<CustomPermission[]>([]);
  const [reportName, setReportName] = useState('');
  const [reportColumnSelectionItems, setReportColumnSelectionItems] =
    useState<Array<ColumnSelectionItem>>();
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [newReportData, setNewReportData] = useState<PropReportDataType>();
  const [savedReportMessage, setSavedReportMessage] = useState('');
  const [reportViewCode, setReportViewCode] = useState<string>();
  const [isDefault, setIsDefault] = useState<boolean>(false);
  const [fetchDataGrid, setFetchDataGrid] = useState<boolean>(false);
  const [showImage, setShowImage] = useState<boolean>(false);
  const [imageURL, setImageURL] = useState<string>('');
  const [fileAttachmentURL, setFileAttachmentURL] = useState<FileAttachmentURLType>(
    SERVICE_ATTACHMENT_FILE_URL_DATA
  );
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const isMobileView = useMediaQuery(ScreenSize.MOBILE);
  const [selectedCellData, setSelectedCellData] = useState<ServiceListDataType | undefined>();
  const [billingData, setBillingData] = useState<ServiceBillingInfo>(SERVICE_BILLING_DATA);
  const [multiSelectTypesList, setMultiSelectTypesList] = useState<ServiceFilterMultiSelectType>();
  const [filterStatusesList, setFilterStatusesList] = useState<LabelValue[]>([]);
  const [columnSelectionData, setColumnSelectionData] = useState(
    checkMobileViewColumnVisibility(columnSelectionItems, isMobile)
  );
  const [firstLoad, setFirstLoad] = useState<boolean>(false);
  const [filterSearch, setFilterSearch] = useState(false);
  const [regionTypeList, setRegionTypeList] = useState<DropdownItem[]>([]);
  const [zoneList, setZoneList] = useState<DropdownItem[]>([]);
  const [listId, setListId] = useState<string>(() => {
    if (defaultReport) return `service-list-${defaultReport}`;
    if (reportCode) return `service-list-${reportCode}`;
    if (importSessionId) return `service-list-${importSessionId}`;
    if (clientCode) return `service-list-${clientCode}-${serviceTypeName}-${serviceStatusGroup}`;
    return 'service-list';
  });

  const useAddNewReportQuery = useAddNewReport();
  const useOverwriteReportQuery = useUpdateReportByCode(reportCode || '');
  const { data: reportQueryData, refetch: refetchGetReportByCodeQuery } = useGetReportByCode(
    reportCode || ''
  );
  const defaultReportData = useGetDefaultReportByReportType(SavedReportTypeList.ServiceList);
  const getUploadedFilePresignedUrlByKey = useGetUploadedFilePresignedUrlByKey(
    fileAttachmentURL.key,
    fileAttachmentURL.mimeType,
    fileAttachmentURL.contentDispositionType
  );
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const regionTypesQuery = useGetRegionTypes();
  const getZones = useGetZones();

  const queryClient = useQueryClient();

  // Custom "is any of" operator to paste a list or series of values to search for service id
  const customNumericIsAnyOfOperator: GridFilterOperator = {
    ...getGridNumericOperators().find((op) => op.value === 'isAnyOf')!,
    InputComponent: MultiPasteFilterInput,
    InputComponentProps: { type: 'number' },
  };

  const customStringIsAnyOfOperator: GridFilterOperator = {
    ...getGridNumericOperators().find((op) => op.value === 'isAnyOf')!,
    InputComponent: MultiPasteFilterInput,
    InputComponentProps: { type: 'string' },
  };

  useEffect(() => {
    setFirstLoad(true);
  }, []);

  useEffect(() => {
    if (!defaultReport && !reportCode) {
      setFilterModel({
        items: getServiceListInitialFilterModel(
          importSessionId,
          clientCode,
          serviceTypeName,
          serviceStatusGroup,
          locationId
        ),
      });
    }
  }, [defaultReport, reportCode, clientCode]);

  useEffect(() => {
    if (defaultReport) setListId(`service-list-${defaultReport}`);
    else if (reportCode) setListId(`service-list-${reportCode}`);
    else if (importSessionId) setListId(`service-list-${importSessionId}`);
    else if (clientCode)
      setListId(`service-list-${clientCode}-${serviceTypeName}-${serviceStatusGroup}`);
    else setListId('service-list');
  }, [reportCode, clientCode, serviceTypeName, serviceStatusGroup]);

  useEffect(() => {
    if (firstLoad) {
      if (defaultReport) {
        setPage(0);
        setPageSize(25);
        setFilterModel({ items: [] });
        setSortModel([]);
        setReportColumnSelectionItems(undefined);
      } else navigate(0);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (regionTypesQuery.data) {
      const regionTypesItems =
        regionTypesQuery?.data?.map((item) => ({
          value: item.name,
          label: item.name,
        })) || [];

      setRegionTypeList(regionTypesItems);
    }
  }, [regionTypesQuery.data]);

  useEffect(() => {
    if (getZones.data) {
      const zoneItems =
        getZones?.data?.map((item) => ({
          value: item.name,
          label: item.name,
        })) || [];

      setZoneList(zoneItems);
    }
  }, [getZones.data]);

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    const filteredColumnSelectionItems = handleServiceListCoulumnPermissions(
      columnSelectionItems,
      permissions
    );
    setColumnSelectionData(filteredColumnSelectionItems);
  }, [columnSelectionItems, permissions]);

  useEffect(() => {
    setIsMobile(isMobileView);
  }, [isMobileView]);

  useEffect(() => {
    setColumns([
      {
        field: 'serviceId',
        headerName: 'Service ID',
        width: 100,
        type: 'number',
        filterOperators: [
          ...getGridNumericOperators().filter((op) => op.value !== 'isAnyOf'),
          customNumericIsAnyOfOperator,
        ],
        valueFormatter: (value: number) => value?.toString(),
        renderCell: (params: GridRenderCellParams) =>
          isUserHasPermission(
            ActionPermissions.Service_Edit_View_Service,
            getUserPermissionsQuery.data
          ) ? (
            <Link href={`services/${params.row.serviceId}`}>{params.row.serviceId}</Link>
          ) : (
            params.row.serviceId
          ),
      },
      ...(isUserHasPermission(
        ActionPermissions.Service_Search_Columns_Service_details,
        getUserPermissionsQuery.data
      )
        ? [
            {
              field: 'serviceTypeName',
              headerName: 'Service Type Name',
              type: 'singleSelect' as GridColType,
              valueOptions: multiSelectTypesList?.serviceTypes,
              valueGetter: (_value: unknown, row: ServiceListDataType) => row?.serviceTypeName,
              width: 200,
            },
            {
              field: 'serviceTypeCode',
              headerName: 'Service Type Code',
              width: 200,
            },
            {
              field: 'actionTypeName',
              headerName: 'Action Type',
              type: 'singleSelect' as GridColType,
              valueOptions: multiSelectTypesList?.actionTypes,
              valueGetter: (_value: unknown, row: ServiceListDataType) => row?.actionTypeName,
              width: 200,
            },
            {
              field: 'processTypeName',
              headerName: 'Process Type',
              type: 'singleSelect' as GridColType,
              valueOptions: multiSelectTypesList?.processTypes,
              valueGetter: (_value: unknown, row: ServiceListDataType) => row?.processTypeName,
              width: 200,
            },
            {
              field: 'serviceParams',
              headerName: 'Service Params',
              width: 200,
            },
            {
              field: 'serviceZone',
              headerName: 'Service Zone',
              type: 'singleSelect' as GridColType,
              valueOptions: zoneList,
              valueGetter: (_valu: unknown, row: ServiceListDataType) => row?.serviceZone,
              width: 200,
            },
            {
              field: 'reference1',
              headerName: 'Reference #1',
              width: 200,
            },
            {
              field: 'reference2',
              headerName: 'Reference #2',
              width: 200,
            },
            {
              field: 'reference3',
              headerName: 'Reference #3',
              width: 200,
            },
            {
              field: 'clientHierarchy',
              headerName: 'Client',
              width: 220,
              renderCell: (params: GridRenderCellParams) => {
                if (params.row.logoPic && params.row.logoPic !== null)
                  return (
                    <LogoImageBox>
                      <img src={params.row.logoPic} alt={params.row.clientHierarchy} />
                    </LogoImageBox>
                  );
                else return params.row.clientHierarchy;
              },
            },
            {
              field: 'clientName',
              headerName: 'Client Name',
              width: 200,
            },
            {
              field: 'clientCode',
              headerName: 'Client Code',
              width: 200,
            },
            {
              field: 'contractName',
              headerName: 'Contract',
              width: 200,
            },
            {
              field: 'contractCode',
              headerName: 'Contract Code',
              width: 200,
            },
            {
              field: 'brandName',
              headerName: 'Brand',
              width: 200,
            },
            {
              field: 'priorityCode',
              headerName: 'Priority',
              width: 150,
              renderCell: (params: GridRenderCellParams) => {
                if (params.row.priority)
                  return (
                    <Chip
                      label={params.row.priority.shortName}
                      backgroundColor={params.row.priority.color}
                      borderColor={params.row.priority.color}
                      textColor="#ffffff"
                    />
                  );
                else return '';
              },
            },
            {
              field: 'priorityDescription',
              headerName: 'Priority Description',
              width: 200,
            },
            {
              field: 'reportedSerial',
              headerName: 'Reported Serial',
              width: 200,
            },
            {
              field: 'reportedModel',
              headerName: 'Reported Model',
              width: 200,
            },
            {
              field: 'reportedModelSerial',
              headerName: 'Reported Model/Serial',
              width: 200,
            },
            {
              field: 'reportedFault',
              headerName: 'Reported Fault',
              width: 200,
            },
            {
              field: 'reportedFaultDescription',
              headerName: 'Reported Fault Description',
              width: 200,
            },
            {
              field: 'reportedFaultDescriptionCombine',
              headerName: 'Reported Fault/Description',
              width: 200,
            },
            {
              field: 'creationNotes',
              headerName: 'Creation Notes',
              width: 300,
              renderCell: (params: GridRenderCellParams) => (
                <Interweave content={params.row.creationNotes} />
              ),
            },
            {
              field: 'createdAt',
              headerName: 'Created At',
              type: 'date' as GridColType,
              width: 200,
              valueGetter: (_value: unknown, row: ServiceListDataType) =>
                row.createdAt && new Date(row.createdAt),
              valueFormatter: (value: string) => formatDate(value),
            },
            {
              field: 'createdBy',
              headerName: 'Created By',
              width: 200,
            },
            {
              field: 'tags',
              headerName: 'Tags',
              width: 300,
              renderCell: (params: GridRenderCellParams) => {
                if (params.row.tags) return <TagCell tags={JSON.parse(params.row.tags)} />;
                else return '';
              },
            },
          ]
        : []),
      ...(isUserHasPermission(
        ActionPermissions.Service_Search_Columns_Statuse_Allocation,
        getUserPermissionsQuery.data
      )
        ? [
            {
              field: 'serviceStatusName',
              headerName: 'Status Name',
              type: 'singleSelect' as GridColType,
              valueOptions: filterStatusesList,
              valueGetter: (_value: unknown, row: ServiceListDataType) => row?.serviceStatusName,
              width: 200,
            },
            {
              field: 'primaryAllocation',
              headerName: 'Primary Allocation',
              width: 200,
            },
            {
              field: 'primaryAllocationNode',
              headerName: 'Primary Allocation Node Name',
              width: 220,
            },
            {
              field: 'secondaryAllocation',
              headerName: 'Secondary Allocation',
              width: 200,
            },
            {
              field: 'secondaryAllocationNode',
              headerName: 'Secondary Allocation Node Name',
              width: 250,
            },
            {
              field: 'scheduledDate',
              headerName: 'Scheduled Date',
              type: 'date' as GridColType,
              width: 200,
              valueGetter: (_value: unknown, row: ServiceListDataType) =>
                row.scheduledDate && new Date(row.scheduledDate),
              valueFormatter: (value: string) => formatDate(value),
            },
            {
              field: 'followUpDate',
              headerName: 'Follow Up Date',
              type: 'date' as GridColType,
              width: 200,
              valueGetter: (_value: unknown, row: ServiceListDataType) =>
                row.followUpDate && new Date(row.followUpDate),
              valueFormatter: (value: string) => formatDate(value),
            },
            {
              field: 'updatedAt',
              headerName: 'Last Updated At',
              type: 'date' as GridColType,
              width: 200,
              valueGetter: (_value: unknown, row: ServiceListDataType) =>
                row.updatedAt && new Date(row.updatedAt),
              valueFormatter: (value: string) => formatDate(value),
            },
            {
              field: 'updatedBy',
              headerName: 'Last Updated By',
              width: 200,
            },
            {
              field: 'lastUpdatedNote',
              headerName: 'Last Updated Notes',
              width: 300,
              renderCell: (params: GridRenderCellParams) => (
                <Interweave content={params.row.lastUpdatedNote} />
              ),
            },
            {
              field: 'specialScheduled',
              headerName: 'Special Scheduled',
              type: 'date' as GridColType,
              width: 200,
              valueGetter: (_value: unknown, row: ServiceListDataType) =>
                row.specialScheduled && new Date(row.specialScheduled),
              valueFormatter: (value: string) => formatDate(value),
            },
            {
              field: 'firstTimeOnSite',
              headerName: 'First Time On Site',
              type: 'date' as GridColType,
              width: 200,
              valueGetter: (_value: unknown, row: ServiceListDataType) =>
                row?.firstTimeOnSite && new Date(row?.firstTimeOnSite),
              valueFormatter: (value: string) => value && formatDate(value),
            },
            {
              field: 'onSiteTime',
              headerName: 'On Site Time',
              width: 150,
              filterable: false,
            },
          ]
        : []),
      ...(isUserHasPermission(
        ActionPermissions.Service_Search_Columns_Structure,
        getUserPermissionsQuery.data
      )
        ? [
            {
              field: 'locationName',
              headerName: 'Location Name',
              width: 350,
            },
            {
              field: 'structureNodeName',
              headerName: 'Structure Node Name',
              width: 200,
            },
            {
              field: 'structureNodeId',
              headerName: 'Structure Node ID',
              width: 130,
              type: 'number' as GridColType,
              valueFormatter: (value: number) => value?.toString(),
            },
            {
              field: 'locationZone',
              headerName: 'Location Zone',
              type: 'singleSelect' as GridColType,
              valueOptions: zoneList,
              valueGetter: (_valu: unknown, row: ServiceListDataType) => row?.locationZone,
              width: 200,
            },
            {
              field: 'structureNodeNotes',
              headerName: 'Structure Node Notes',
              width: 200,
            },
            {
              field: 'subLocationName',
              headerName: 'Sublocation Name',
              width: 200,
            },
            {
              field: 'subLocationId',
              headerName: 'Sublocation ID',
              width: 130,
              type: 'number' as GridColType,
              valueFormatter: (value: number) => value?.toString(),
            },
            {
              field: 'serviceAddress',
              headerName: 'Service Address',
              width: 200,
            },
            {
              field: 'serviceAddressLine1',
              headerName: 'Line 1',
              width: 100,
            },
            {
              field: 'serviceAddressLine2',
              headerName: 'Line 2',
              width: 100,
            },
            {
              field: 'serviceAddressSuburb',
              headerName: 'Suburb',
              width: 100,
            },
            {
              field: 'serviceAddressCity',
              headerName: 'City',
              width: 100,
            },
            {
              field: 'serviceAddressStateName',
              headerName: 'State',
              width: 100,
            },
            {
              field: 'serviceAddressPostCode',
              headerName: 'Post Code',
              width: 100,
            },
            {
              field: 'serviceAddressCountryName',
              headerName: 'Country',
              width: 120,
            },
            {
              field: 'mainAddress',
              headerName: 'Main Address',
              width: 200,
            },
            {
              field: 'logisticsAddress',
              headerName: 'Logistics Address',
              width: 200,
            },
            {
              field: 'regionType',
              headerName: 'Region Type',
              type: 'singleSelect' as GridColType,
              valueOptions: regionTypeList,
              valueGetter: (_valu: unknown, row: ServiceListDataType) => row?.regionType,
              width: 200,
            },
            {
              field: 'serviceContact',
              headerName: 'Service Contact',
              width: 200,
            },
            {
              field: 'mainContact',
              headerName: 'Main Contact',
              width: 200,
            },
            {
              field: 'nodeReference1',
              headerName: 'Node Reference #1',
              width: 200,
            },
            {
              field: 'nodeReference2',
              headerName: 'Node Reference #2',
              width: 200,
            },
            {
              field: 'nodeReference3',
              headerName: 'Node Reference #3',
              width: 200,
            },
            {
              field: 'nodeSiteSchema',
              headerName: 'Node Site Schema',
              width: 300,
            },
            {
              field: 'tradingHours',
              headerName: 'Trading Hours',
              width: 300,
              renderCell: (params: GridRenderCellParams) => (
                <Interweave content={params.row.tradingHours} />
              ),
            },
          ]
        : []),
      ...(isUserHasPermission(
        ActionPermissions.Service_Search_Columns_Part_Details,
        getUserPermissionsQuery.data
      )
        ? [
            {
              field: 'partTypeId',
              headerName: 'Part Type ID',
              width: 100,
              type: 'number' as GridColType,
              valueFormatter: (value: number) => value?.toString(),
            },
            {
              field: 'partTypeName',
              headerName: 'Part Type Name',
              width: 200,
            },
            {
              field: 'partNo',
              headerName: 'Part No.',
              width: 100,
            },
            {
              field: 'partSerial1',
              headerName: 'Serial 1',
              width: 200,
              filterOperators: [
                ...getGridNumericOperators().filter((op) => op.value !== 'isAnyOf'),
                customStringIsAnyOfOperator,
              ],
            },
            {
              field: 'partSerial2',
              headerName: 'Serial 2',
              width: 200,
              filterOperators: [
                ...getGridNumericOperators().filter((op) => op.value !== 'isAnyOf'),
                customStringIsAnyOfOperator,
              ],
            },
            {
              field: 'partSerial3',
              headerName: 'Serial 3',
              width: 200,
              filterOperators: [
                ...getGridNumericOperators().filter((op) => op.value !== 'isAnyOf'),
                customStringIsAnyOfOperator,
              ],
            },
            {
              field: 'softwareVersion',
              headerName: 'Software Ver',
              width: 200,
            },
            {
              field: 'firmwareVersion',
              headerName: 'Firmware Ver',
              width: 200,
            },
            {
              field: 'hardwareRevision',
              headerName: 'Hardware Rev',
              width: 200,
            },
            {
              field: 'fleetTag',
              headerName: 'Fleet Tag',
              width: 200,
            },
          ]
        : []),
      ...(isUserHasPermission(
        ActionPermissions.Service_Search_Columns_Linked_Records,
        getUserPermissionsQuery.data
      )
        ? [
            {
              field: 'linkedServices',
              headerName: 'Linked Service #',
              width: 200,
              renderCell: (params: GridRenderCellParams) => {
                if (params.row.linkedServices) {
                  return params.row.linkedServices.split(', ').map((serviceID: string) => (
                    <>
                      <Link key={serviceID} href={`services/${serviceID}`}>
                        {serviceID}
                      </Link>
                      &nbsp;
                    </>
                  ));
                } else {
                  return '';
                }
              },
            },
            {
              field: 'linkedShipments',
              headerName: 'Linked Shipments',
              width: 200,
              renderCell: (params: GridRenderCellParams) => {
                if (params.row.linkedShipments) {
                  return params.row.linkedShipments.split(', ').map((shipmentID: string) => (
                    <>
                      <Link key={shipmentID} href={`shipments/${shipmentID}`}>
                        {shipmentID}
                      </Link>
                      &nbsp;
                    </>
                  ));
                } else {
                  return '';
                }
              },
            },
            {
              field: 'linkedShipmentConnotes',
              headerName: 'Linked Shipments Connotes',
              width: 200,
            },
          ]
        : []),
      ...(isUserHasPermission(
        ActionPermissions.Service_Search_Columns_Progress_Data,
        getUserPermissionsQuery.data
      )
        ? [
            {
              field: 'daysOpen',
              headerName: 'Days Open',
              width: 100,
              type: 'number' as GridColType,
              valueFormatter: (value: number) => value?.toString(),
            },
            {
              field: 'closedAt',
              headerName: 'Closed At',
              type: 'date' as GridColType,
              width: 200,
              valueGetter: (_value: unknown, row: ServiceListDataType) =>
                row.closedAt && new Date(row.closedAt),
              valueFormatter: (value: string) => formatDate(value),
            },
            {
              field: 'attendanceCount',
              headerName: '# of Attendances',
              width: 150,
              type: 'number' as GridColType,
              valueFormatter: (value: number) => value?.toString(),
            },
            {
              field: 'reopensCount',
              headerName: '# of Re-opens',
              width: 130,
              type: 'number' as GridColType,
              valueFormatter: (value: number) => value?.toString(),
            },
            {
              field: 'uniqueTechniciansCount',
              headerName: '# of Unique Technicians',
              width: 180,
              type: 'number' as GridColType,
              valueFormatter: (value: number) => value?.toString(),
            },
            {
              field: 'observedFault',
              headerName: 'Observed Fault',
              width: 200,
            },
            {
              field: 'observedFaultDescription',
              headerName: 'Observed Fault Description',
              width: 200,
            },
            {
              field: 'solutionAction',
              headerName: 'Solution Action',
              width: 200,
            },
            {
              field: 'solutionActionPartType',
              headerName: 'Solution Action Part Type Name',
              width: 230,
            },
            {
              field: 'serial1sIn',
              headerName: "Serial 1's In",
              width: 200,
            },
            {
              field: 'serialisedPartTypesIn',
              headerName: "Serialised Part Type Name's In",
              width: 270,
            },
            {
              field: 'serial1sOut',
              headerName: "Serial 1's Out",
              width: 200,
            },
            {
              field: 'serialisedPartTypesOut',
              headerName: "Serialised Part Type Name's Out",
              width: 270,
            },
            {
              field: 'nonSerialisedPartTypesIn',
              headerName: "Non Serialised Part Type Name's In",
              width: 270,
              renderCell: (params: GridRenderCellParams) => (
                <Interweave content={params.row.nonSerialisedPartTypesIn} />
              ),
            },
            {
              field: 'attachments',
              headerName: 'Attachments',
              width: 300,
              renderCell: (params: GridRenderCellParams) => {
                return (
                  <Grid container spacing={1} direction="row">
                    {params.row.attachments &&
                      JSON.parse(params.row.attachments)?.map(
                        (attachment: { fileName: string; key: string }, index: number) => (
                          <Box key={index} onClick={() => handleOpen(attachment)}>
                            <Interweave content={formatServiceAttachmentsData(attachment)} />
                            {index !== JSON.parse(params.row.attachments).length - 1 && ', \n'}
                          </Box>
                        )
                      )}
                  </Grid>
                );
              },
            },
          ]
        : []),
      ...(isUserHasPermission(
        ActionPermissions.Service_Search_Columns_SLA,
        getUserPermissionsQuery.data
      )
        ? [
            {
              field: 'clientSLASummary',
              headerName: 'Client SLA Summary',
              width: 200,
              renderCell: (params: GridRenderCellParams) => {
                return (
                  <Typography
                    textColor={
                      params.row.clientSLASummary?.split(' , ')[1] === 'breached'
                        ? COLORS.SLABreached
                        : COLORS.SLAComplete
                    }
                  >
                    {getDateFromTimestamp(params.row.clientSLASummary?.split(' , ')[0])}
                  </Typography>
                );
              },
            },
            {
              field: 'opsSLASummary',
              headerName: 'Ops SLA Summary',
              width: 200,
              renderCell: (params: GridRenderCellParams) => {
                return (
                  <Typography
                    textColor={
                      params.row.opsSLASummary?.split(' , ')[1] === 'breached'
                        ? COLORS.SLABreached
                        : COLORS.SLAComplete
                    }
                  >
                    {getDateFromTimestamp(params.row.opsSLASummary?.split(' , ')[0])}
                  </Typography>
                );
              },
            },
          ]
        : []),
      ...(isUserHasPermission(
        ActionPermissions.Service_Search_Columns_Interactions,
        getUserPermissionsQuery.data
      )
        ? [
            {
              field: 'interactionTextbox',
              headerName: 'Interaction Textbox',
              width: 200,
              renderCell: (params: GridRenderCellParams) => (
                <Interweave content={params.row.interactionTextbox} />
              ),
            },
            {
              field: 'interactionRichTextbox',
              headerName: 'Interaction Rich Textbox',
              width: 250,
              renderCell: (params: GridRenderCellParams) => (
                <Interweave content={params.row.interactionRichTextbox} />
              ),
            },
            {
              field: 'interactionDropdown',
              headerName: 'Interaction Combobox',
              width: 200,
              renderCell: (params: GridRenderCellParams) => (
                <Interweave content={params.row.interactionDropdown} />
              ),
            },
            {
              field: 'interactionRadiobutton',
              headerName: 'Interaction Radiobox',
              width: 200,
              renderCell: (params: GridRenderCellParams) => (
                <Interweave content={params.row.interactionRadiobutton} />
              ),
            },
            {
              field: 'interactionCheckbox',
              headerName: 'Interaction Checkbox',
              width: 200,
              renderCell: (params: GridRenderCellParams) => (
                <Interweave content={params.row.interactionCheckbox} />
              ),
            },
            {
              field: 'interactionGetParameter',
              headerName: 'Interaction Get Parameter',
              width: 300,
              renderCell: (params: GridRenderCellParams) => (
                <Interweave content={params.row.interactionGetParameter} />
              ),
            },
            {
              field: 'pendedParts',
              headerName: 'Pended Parts',
              width: 250,
            },
            {
              field: 'pendedNotes',
              headerName: 'Pended Notes',
              width: 400,
              renderCell: (params: GridRenderCellParams) => (
                <Interweave content={params.row.pendedNotes} />
              ),
            },
          ]
        : []),
      ...(isUserHasPermission(
        ActionPermissions.Service_Search_Columns_Admin_And_Billing,
        getUserPermissionsQuery.data
      )
        ? [
            {
              field: 'reviewed',
              headerName: 'Reviewed',
              type: 'singleSelect' as GridColType,
              valueOptions: [
                { value: true, label: 'True' },
                { value: false, label: 'False' },
              ],
              width: 100,
              renderCell: (params: GridRenderCellParams) => {
                return (
                  <SwitchGrid>
                    <FormControlLabel
                      control={<Switch color={ColorProps.Success} checked={params.row.reviewed} />}
                      label=""
                    />
                  </SwitchGrid>
                );
              },
              editable: true,
              renderEditCell: () => {
                return (
                  <SwitchGrid>
                    <FormControlLabel
                      control={
                        <Switch
                          color={ColorProps.Success}
                          checked={billingData.isReviewed}
                          onChange={(e) =>
                            setBillingData({
                              ...billingData,
                              isReviewed: e.target.checked,
                              updated: true,
                            })
                          }
                        />
                      }
                      label=""
                    />
                  </SwitchGrid>
                );
              },
            },
            {
              field: 'billableType',
              headerName: 'Billable Type',
              width: 200,
              editable: true,
              renderEditCell: () => {
                return (
                  <SelectMenu
                    id="billable-type"
                    label="Billable Type"
                    items={getBillingTypesDropdownData(getServiceBillingTypes?.data || [])}
                    selectedValue={billingData.billingTypeCode}
                    onChange={(code) =>
                      setBillingData({ ...billingData, billingTypeCode: code, updated: true })
                    }
                  />
                );
              },
            },
            {
              field: 'clientInvoiced',
              headerName: 'Client Invoiced',
              type: 'singleSelect' as GridColType,
              valueOptions: [
                { value: true, label: 'True' },
                { value: false, label: 'False' },
              ],
              width: 100,
              renderCell: (params: GridRenderCellParams) => {
                return (
                  <SwitchGrid>
                    <FormControlLabel
                      control={
                        <Switch color={ColorProps.Success} checked={params.row.clientInvoiced} />
                      }
                      label=""
                    />
                  </SwitchGrid>
                );
              },
              editable: true,
              renderEditCell: () => {
                return (
                  <SwitchGrid>
                    <FormControlLabel
                      control={
                        <Switch
                          color={ColorProps.Success}
                          checked={billingData?.isClientInvoiced}
                          onChange={(e) =>
                            setBillingData({
                              ...billingData,
                              isClientInvoiced: e.target.checked,
                              updated: true,
                            })
                          }
                        />
                      }
                      label=""
                    />
                  </SwitchGrid>
                );
              },
            },
            {
              field: 'billingNotes',
              headerName: 'Billing Notes',
              width: 200,
              renderCell: (params: GridRenderCellParams) => (
                <Interweave content={params.row.billingNotes} />
              ),
              editable: true,
              renderEditCell: () => {
                return (
                  <EditField
                    isEditable={true}
                    value={billingData.billingNotes}
                    onChange={(text) =>
                      setBillingData({ ...billingData, billingNotes: text, updated: true })
                    }
                  />
                );
              },
            },
            {
              field: 'internalInvoices',
              headerName: 'Internal Invoices',
              width: 400,
              editable: true,
              renderEditCell: () => {
                return (
                  <InvoiceList
                    heading="Internal Invoices"
                    isInlineEdit
                    invoiceListValues={getInvoicesByType(
                      billingData.invoices,
                      InvoiceType.Internal
                    )}
                    onChange={(value) =>
                      setBillingData({
                        ...billingData,
                        invoices: getInvoicesByType(
                          billingData.invoices,
                          InvoiceType.External
                        ).concat(value),
                      })
                    }
                    onCreate={(value) =>
                      setBillingData({
                        ...billingData,
                        invoices: [
                          ...billingData.invoices,
                          { ...value, invoiceType: InvoiceType.Internal },
                        ],
                        updated: true,
                      })
                    }
                    onDelete={(value) =>
                      setBillingData({
                        ...billingData,
                        invoices: getInvoicesByType(
                          billingData.invoices,
                          InvoiceType.External
                        ).concat(value),
                      })
                    }
                  />
                );
              },
            },
            {
              field: 'externalInvoices',
              headerName: 'External Invoices',
              width: 400,
              editable: true,
              renderEditCell: () => {
                return (
                  <InvoiceList
                    heading="External Invoices"
                    isInlineEdit
                    invoiceListValues={getInvoicesByType(
                      billingData.invoices,
                      InvoiceType.External
                    )}
                    onChange={(value) =>
                      setBillingData({
                        ...billingData,
                        invoices: getInvoicesByType(
                          billingData.invoices,
                          InvoiceType.Internal
                        ).concat(value),
                      })
                    }
                    onCreate={(value) =>
                      setBillingData({
                        ...billingData,
                        invoices: [
                          ...billingData.invoices,
                          { ...value, invoiceType: InvoiceType.External },
                        ],
                        updated: true,
                      })
                    }
                    onDelete={(value) =>
                      setBillingData({
                        ...billingData,
                        invoices: getInvoicesByType(
                          billingData.invoices,
                          InvoiceType.Internal
                        ).concat(value),
                      })
                    }
                  />
                );
              },
            },
          ]
        : []),
      {
        field: 'allNotes',
        headerName: 'All Notes',
        width: 500,
        renderCell: (params) => <Interweave content={params.row.allNotes} />,
      },
      {
        field: 'importSessionId',
        headerName: 'Import Session ID',
        width: 130,
      },
      {
        field: MOBILE_DATA_GRID_COLUMN,
        headerName: '',
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams) => {
          if (row.isHeaderRow && row.rowHeaderTitle) {
            return (
              <HeaderWrapper variant={TypographyVariantProps.Subtitle1}>
                {row.rowHeaderTitle}
              </HeaderWrapper>
            );
          }
          return <ServiceListMobileView data={row} />;
        },
        sortable: false,
        filterable: false,
      },
    ]);
  }, [
    permissions,
    billingData,
    multiSelectTypesList,
    filterStatusesList,
    regionTypeList,
    zoneList,
  ]);

  const onFilterChange = useCallback((newFilterModel: GridFilterModel) => {
    if (
      (importSessionId || (clientCode && serviceTypeName && serviceStatusGroup)) &&
      !newFilterModel.items.length
    ) {
      setFilterModel({ items: [...initialFilterModel, ...newFilterModel.items] });
    } else if (
      defaultReportData.data &&
      defaultReportData.data[0]?.dataGridProperties?.filterModel &&
      !newFilterModel.items.length
    ) {
      setFilterModel(
        defaultReportData.data && defaultReportData.data[0]?.dataGridProperties?.filterModel
      );
    } else if (reportQueryData?.dataGridProperties.filterModel && !newFilterModel.items.length) {
      setFilterModel(reportQueryData.dataGridProperties.filterModel);
    } else {
      setFilterModel(newFilterModel);
    }
  }, []);

  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    setSortModel(sortModel);
  }, []);

  const handleSelectionModelChange = useCallback((selectionModel: GridRowSelectionModel) => {
    setSelectionModel(selectionModel);
  }, []);

  const getServicePaginatedData = useGetServicePaginatedData(
    {
      filterModel,
      page,
      pageSize,
      sortModel,
      locationId,
      includeChildRecords,
      isFromStructures,
      defaultReport,
      multiSearchQuery,
    },
    reportColumnSelectionItems || columnSelectionData
  );
  const getServicesStatuses = useGetServicesStatuses();
  const getActionTypes = useGetActionTypes();
  const getGetProcessTypes = useGetProcessTypes();
  const updateBulkService = useUpdateBulkServiceList();
  const getBulkServicesByIds = useGetBulkServicesByIds();
  const getServiceBillingTypes = useGetServiceBillingTypes();
  const getBillingInfo = useGetBillingInfo(selectedCellData?.serviceId || 0);
  const updateBillingInfo = useUpdateBillingInfo(selectedCellData?.serviceId || 0);
  const getServiceActionProcessTypes = useGetServiceActionProcessTypes();

  useEffect(() => {
    !filterStatusesList.length && getServicesStatuses.refetch();
  }, [filterStatusesList]);

  useEffect(() => {
    selectedCellData && getBillingInfo.refetch();
  }, [selectedCellData]);

  useEffect(() => {
    selectedCellData &&
      setBillingData({ ...(getBillingInfo.data || SERVICE_BILLING_DATA), updated: false });
  }, [getBillingInfo.data, selectedCellData]);

  useEffect(() => {
    billingData.updated && updateBillingData();
    if (!billingData.updated && billingData.updated !== undefined) {
      const cellMode = apiRef.current.getCellMode(
        selectedCellData?.id || 0,
        selectedCellData?.fieldName || ''
      );
      selectedCellData &&
        cellMode === GridCellModes.View &&
        apiRef.current.startCellEditMode({
          id: selectedCellData.id,
          field: selectedCellData.fieldName || '',
        });
    }
  }, [billingData]);

  useEffect(() => {
    !reportCode && defaultReportData.refetch();
  }, [SavedReportTypeList.ServiceList]);

  useEffect(() => {
    if (
      !reportCode &&
      !importSessionId &&
      !clientCode &&
      defaultReportData.data &&
      defaultReportData.data?.length > 0 &&
      !locationId &&
      !defaultReport
    ) {
      sessionStorage.setItem(
        'service-list',
        JSON.stringify(defaultReportData.data[0].dataGridProperties?.filterModel)
      );
      setFilterModel(defaultReportData.data[0].dataGridProperties?.filterModel);
      setSortModel(defaultReportData.data[0].dataGridProperties?.sortModel || []);
      setReportColumnSelectionItems(defaultReportData.data[0].columnSelection);
      setPageSize(defaultReportData.data[0].dataGridProperties?.pageSize);

      if (defaultReportData.data[0].dataGridProperties?.columnOrder && columns) {
        const updatedColumns = defaultReportData.data[0].dataGridProperties?.columnOrder.map(
          (orderCol) => {
            const originalColumn = columns.find((col) => col.field === orderCol.field);
            return originalColumn ? { ...originalColumn } : orderCol;
          }
        );

        setColumns(updatedColumns);
      }
    }
  }, [defaultReportData.data, reportCode]);

  useEffect(() => {
    if (reportCode) {
      refetchGetReportByCodeQuery();
      setReportViewCode(reportCode);
    }
  }, [reportCode, fetchDataGrid]);

  useEffect(() => {
    if (reportCode && reportQueryData && firstLoad) {
      document.title = `${reportQueryData.name} (${reportCode}) | List Clients | ${PLATFORM_NAME}`;

      const storedFilterModel = sessionStorage.getItem(`service-list-${reportCode}`);
      setFilterModel(
        storedFilterModel
          ? JSON.parse(storedFilterModel)
          : reportQueryData.dataGridProperties.filterModel
      );
      setSortModel(reportQueryData.dataGridProperties.sortModel || []);
      setReportColumnSelectionItems(reportQueryData.columnSelection);
      setPageSize(reportQueryData.dataGridProperties.pageSize);
      setReportName(reportQueryData.name);
      setIsDefault(reportQueryData.isDefault);

      if (reportQueryData.dataGridProperties?.columnOrder && columns) {
        const updatedColumns = reportQueryData.dataGridProperties?.columnOrder.map((orderCol) => {
          const originalColumn = columns.find((col) => col.field === orderCol.field);
          return originalColumn ? { ...originalColumn } : orderCol;
        });

        setColumns(updatedColumns);
      }

      setFirstLoad(false);
    }
  }, [reportQueryData, reportViewCode, columns, firstLoad]);

  useEffect(() => {
    setNewReportData({
      dataGridProperties: {
        filterModel: filterModel,
        pageSize: pageSize,
        sortModel: sortModel || [],
        columnOrder: columns,
      },
      reportTypeName: SavedReportTypeList.ServiceList,
      reportName: reportName,
      isDefault: reportQueryData?.isDefault,
      id: reportQueryData?.id,
      reportOwner: reportQueryData?.createdBy,
      columnSelection: reportColumnSelectionItems,
    });
  }, [filterModel, pageSize, isDefault, reportName, sortModel, columns]);

  useEffect(() => {
    if (
      (!reportCode || (reportCode && reportQueryData)) &&
      (filterModel.items.length === 0 ||
        checkListPagetFilterAvailability(filterModel) ||
        sortModel?.some((sort) => sort.field))
    ) {
      getServicePaginatedData.refetch();
    }
  }, [
    filterModel,
    page,
    pageSize,
    sortModel,
    reportCode,
    isDefault,
    reportQueryData,
    reportColumnSelectionItems,
    locationId,
    includeChildRecords,
    isFromStructures,
    filterSearch,
    multiSearchQuery,
  ]);

  useEffect(() => {
    const selectedValues = services.reduce(
      (accumulator: SelectedServiceListDataType[], currentValue) => {
        if (selectionModel.includes(currentValue.id) && currentValue.serviceId) {
          const item = {
            id: currentValue.serviceId,
            isClosed: currentValue.isClosedServiceStatus,
            image: currentValue.logoPic || '',
          };
          return [...accumulator, item];
        }
        return accumulator;
      },
      []
    );

    setSelectedServices([...selectedValues]);
  }, [selectionModel]);

  useEffect(() => {
    if (getServicePaginatedData.data) {
      // filter services logged against location based on search param 'locationId'
      const results = getServicePaginatedData.data.data;

      if (isMobile && results) {
        const clonedResults: ServiceListDataType[] = JSON.parse(JSON.stringify(results));
        const loggedinUserCheckedInServices: ServiceListDataType[] = JSON.parse(
          JSON.stringify(clonedResults.filter((service) => service.isCheckedIn) || [])
        );
        const allocatedServices: ServiceListDataType[] = JSON.parse(
          JSON.stringify(clonedResults.filter((service) => service.isAllocated) || [])
        );
        const allocatedAboveMeServices: ServiceListDataType[] = JSON.parse(
          JSON.stringify(clonedResults.filter((service) => service.isAllocatedAboveMe) || [])
        );
        let groupedServices: ServiceListDataType[] = [];
        let checkedInServicesHeaderRow: ServiceListDataType;
        let allocatedServicesHeaderRow: ServiceListDataType;
        let allocatedAboveMeServicesHeaderRow: ServiceListDataType;

        if (loggedinUserCheckedInServices?.length) {
          checkedInServicesHeaderRow = {
            id: Math.random(),
            isClosedServiceStatus: false,
            isHeaderRow: true,
            rowHeaderTitle: 'Checked In',
          };

          loggedinUserCheckedInServices.map((service) => (service.id = Math.random()));
          groupedServices = [checkedInServicesHeaderRow, ...loggedinUserCheckedInServices];
        }
        if (allocatedServices?.length) {
          allocatedServicesHeaderRow = {
            id: Math.random(),
            isClosedServiceStatus: false,
            isHeaderRow: true,
            rowHeaderTitle: 'Allocated To Me',
          };

          allocatedServices.map((service) => (service.id = Math.random()));
          groupedServices = [...groupedServices, allocatedServicesHeaderRow, ...allocatedServices];
        }

        if (allocatedAboveMeServices?.length) {
          allocatedAboveMeServicesHeaderRow = {
            id: Math.random(),
            isClosedServiceStatus: false,
            isHeaderRow: true,
            rowHeaderTitle: 'Allocated Above Me',
          };

          allocatedAboveMeServices.map((service) => (service.id = Math.random()));
          groupedServices = [
            ...groupedServices,
            allocatedAboveMeServicesHeaderRow,
            ...allocatedAboveMeServices,
          ];
        }

        if (groupedServices.length) {
          const otherServicesHeaderRow: ServiceListDataType = {
            id: Math.random(),
            isClosedServiceStatus: false,
            isHeaderRow: true,
            rowHeaderTitle: 'Other',
          };

          const otherServices: ServiceListDataType[] = clonedResults?.filter((service) => {
            return (
              !loggedinUserCheckedInServices.some(
                (checkedInService) => checkedInService.serviceId === service.serviceId
              ) &&
              !allocatedServices.some(
                (allocatedService) => allocatedService.serviceId === service.serviceId
              ) &&
              !allocatedAboveMeServices.some(
                (allocatedService) => allocatedService.serviceId === service.serviceId
              )
            );
          });

          groupedServices = [...groupedServices, otherServicesHeaderRow, ...otherServices];
          setServices(groupedServices);
        } else {
          setServices(results || []);
        }
      } else {
        setServices(results || []);
      }

      setRowCountState(getServicePaginatedData.data.total || 0);
    }
  }, [getServicePaginatedData.data, isMobile]);

  useEffect(() => {
    if (getServicesStatuses.data) {
      setStatusesList(
        getServicesStatuses.data
          .filter((status) => status.isActive)
          .map((listData) => ({
            value: listData.code,
            label: listData.name,
            disabled: !listData.isActive,
          }))
      );
      const openStatuses = getServicesStatuses.data
        .filter((status) => status.isActive && !status.isClosed)
        .map((listData) => ({
          value: listData.name,
          label: listData.name,
          disabled: !listData.isActive,
        }));
      const closedStatuses = getServicesStatuses.data
        .filter((status) => status.isActive && status.isClosed)
        .map((listData) => ({
          value: listData.name,
          label: listData.name,
          disabled: !listData.isActive,
        }));
      setFilterStatusesList(openStatuses.concat(closedStatuses));
    }
  }, [getServicesStatuses.data]);

  useEffect(() => {
    if (getServiceActionProcessTypes.data) {
      const filterData = getServiceActionProcessTypesForMultiSelectFilters(
        getServiceActionProcessTypes.data
      );
      setMultiSelectTypesList({
        serviceTypes: filterData.serviceTypesData,
        actionTypes: filterData.actionTypesData,
        processTypes: filterData.processTypesData,
      });
    }
  }, [getServiceActionProcessTypes.data]);

  useEffect(() => {
    if (getActionTypes.data) {
      setActionTypesList(
        getActionTypes.data.map((listData) => ({ value: listData.code, label: listData.name }))
      );
    }
  }, [getActionTypes.data]);

  useEffect(() => {
    if (getGetProcessTypes.data) {
      setProcessTypesList(
        getGetProcessTypes.data.map((listData) => ({ value: listData.code, label: listData.name }))
      );
    }
  }, [getGetProcessTypes.data]);

  useEffect(() => {
    imageURL && setShowImage(true);
  }, [imageURL]);

  useEffect(() => {
    fileAttachmentURL?.key && getUploadedFilePresignedUrlByKey.refetch();
  }, [fileAttachmentURL?.key]);

  useEffect(() => {
    if (getUploadedFilePresignedUrlByKey.data?.url) {
      if (
        fileAttachmentURL.mimeType === FileMimeTypes.PNG ||
        fileAttachmentURL.mimeType === FileMimeTypes.JPEG ||
        fileAttachmentURL.mimeType === FileMimeTypes.JPG
      ) {
        setImageURL(getUploadedFilePresignedUrlByKey.data.url);
      } else {
        fileAttachmentURL.fileName &&
          downloadFile(getUploadedFilePresignedUrlByKey.data?.url, fileAttachmentURL.fileName);
      }
      setFileAttachmentURL(SERVICE_ATTACHMENT_FILE_URL_DATA);
      queryClient.removeQueries({
        queryKey: [
          UploadedFilesKey,
          fileAttachmentURL.key,
          fileAttachmentURL.mimeType,
          fileAttachmentURL.contentDispositionType,
        ],
      });
    }
  }, [getUploadedFilePresignedUrlByKey.data]);

  const bulkEditServiceDropDownsDataset = {
    actionTypeItems: actionTypesList,
    processTypeItems: processTypesList,
    statusItems: statusesList,
    linkedFieldItems: linkedFieldItems,
  };

  const bulkUpdateData = async (BulkServiceUpdateData: BulkEditServiceDataType) => {
    setBulkUpdateMessage('');
    const responce = (await updateBulkService.mutateAsync(BulkServiceUpdateData)) as unknown as {
      message?: string;
    };
    setBulkUpdateMessage(responce?.message ?? 'Successfully Updated');
    getServicePaginatedData.refetch();
  };

  const retrieveDataByImportedBulkServiceIds = async (importedBulkServiceIds: number[]) => {
    const importServiceIdObject = {
      affectedServiceIds: importedBulkServiceIds,
    };
    const serviceData = (await getBulkServicesByIds.mutateAsync(
      importServiceIdObject
    )) as unknown as SelectedServiceListDataType[];
    setSelectedServicesUsingCsv(serviceData);
  };

  const handleColumnsChange = (items: Array<ColumnSelectionItem>) => {
    setReportColumnSelectionItems(items);
  };

  const savedReport = async (newReportData: NewReportDataType) => {
    setSavedReportMessage('');
    const response: ReportResponse = await useAddNewReportQuery.mutateAsync(newReportData);
    setSavedReportMessage(response?.message ? response?.message : 'Successfully Created');
    const reportURL = generateReportURL(window.location.pathname, response.code);
    setOpenToast(true);
    navigate(reportURL, { replace: true });
  };

  const overwriteReport = async (overwriteReportData: OverwriteReportDataType) => {
    setSavedReportMessage('');
    const response: ReportResponse = await useOverwriteReportQuery.mutateAsync(overwriteReportData);
    setSavedReportMessage(response?.message ? response?.message : 'Successfully Updated');
    const reportURL = generateReportURL(window.location.pathname, response.code);
    setOpenToast(true);
    navigate(reportURL, { replace: true });
  };

  const handleOpen = (attachmentsData: { fileName: string; key: string }) => {
    const URL_data = getServiceAttachmentURLData(attachmentsData);
    if (URL_data) {
      const fileAttachmentData = {
        key: URL_data.key,
        mimeType: URL_data.mimeType,
        contentDispositionType: URL_data.contentDispositionType,
        fileName: URL_data.fileName,
      };
      setFileAttachmentURL(fileAttachmentData);
    }
  };

  const handleCellClick = (params: GridCellParams) => {
    const clickedCellMode = apiRef.current.getCellMode(params.id, params.field);
    const selectedCellMode = apiRef.current.getCellMode(
      selectedCellData?.id || 0,
      selectedCellData?.fieldName || ''
    );

    // Cell mode should be in view mode to set it to edit mode
    if (clickedCellMode === GridCellModes.View) {
      selectedCellMode === GridCellModes.Edit &&
        apiRef.current.stopCellEditMode({
          id: selectedCellData?.id || 0,
          field: selectedCellData?.fieldName || '',
        });
      // Check whether the clicked cell is an editable cell and set the new cell data
      if (params.isEditable) {
        setSelectedCellData({ ...params.row, fieldName: params.field });
      } else {
        selectedCellData && setSelectedCellData(undefined);
        billingData && setBillingData(SERVICE_BILLING_DATA);
      }
    }
  };

  const updateBillingData = async () => {
    await updateBillingInfo.mutateAsync(billingData);
    apiRef.current.stopCellEditMode({
      id: selectedCellData?.id || 0,
      field: selectedCellData?.fieldName || '',
    });
    setSelectedCellData(undefined);
    setBillingData(SERVICE_BILLING_DATA);
    getServicePaginatedData.refetch();
  };

  const handleImageClose = () => {
    setShowImage(false);
    setImageURL('');
  };

  return (
    <>
      <GridContainer isMobile={isMobileView}>
        <Grid>
          <Snackbar
            open={openToast}
            autoHideDuration={4000}
            message={savedReportMessage ? savedReportMessage : 'Successfully Created'}
            onClose={() => setOpenToast(false)}
          />
        </Grid>
        <CustomDataGrid
          id={listId}
          columns={getServicesListFilteredColumns(reportColumnSelectionItems || [], columns)}
          rows={services}
          exportFileName={ListType.ServiceList}
          enableCSVExport={isUserHasPermission(ActionPermissions.Service_Export, permissions)}
          columnSelectionItems={
            reportColumnSelectionItems ? reportColumnSelectionItems : columnSelectionData
          }
          filterMode={GridFeatureModeConstant.server}
          onFilterModelChange={(filterModel: GridFilterModel) => {
            onFilterChange(filterModel);
          }}
          loading={getServicePaginatedData.isLoading || getServicePaginatedData.isRefetching}
          rowCount={rowCountState}
          rowsPerPageOptions={[25, 50, 100]}
          pagination={true}
          page={page}
          pageSize={pageSize}
          paginationMode={GridFeatureModeConstant.server}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          sortingMode={GridFeatureModeConstant.server}
          onSortModelChange={handleSortModelChange}
          selectionModel={selectionModel}
          onSelectionModelChange={handleSelectionModelChange}
          componentsProps={{
            toolbar: {
              printOptions: { disableToolbarButton: true },
            },
          }}
          enableBulkUpdate={isUserHasPermission(
            ActionPermissions.Service_Bulk_Update_Search,
            permissions
          )}
          bulkEditServiceDropDownsDataset={bulkEditServiceDropDownsDataset}
          selectedSevicesForBulkUpdate={
            selectedServices.length === 0 ? selectedServicesUsingCsv : selectedServices
          }
          bulkUpdateToastMessage={BulkUpdateMessage ?? ''}
          handleBulkUpdateSave={(BulkServiceUpdateData: BulkEditServiceDataType) => {
            bulkUpdateData(BulkServiceUpdateData);
          }}
          handleBulkUpdateImportedData={(data: object[]) => {
            const importedServiceIds: number[] = data.map((item) => {
              const row = item as Record<string, string>;
              const id = parseInt(row.Service_Id);
              return id;
            });
            retrieveDataByImportedBulkServiceIds(importedServiceIds);
          }}
          filterModel={filterModel}
          newReportData={newReportData}
          handleSavedReport={(newReportData: NewReportDataType) => savedReport(newReportData)}
          handleOverwriteReport={(overwriteReportData: OverwriteReportDataType) =>
            overwriteReport(overwriteReportData)
          }
          enableSavedReport={true}
          reportCode={reportViewCode}
          handleColumnsChange={handleColumnsChange}
          handleRefetch={() => setFetchDataGrid(!fetchDataGrid)}
          getRowHeight={() => 'auto'}
          sortModel={sortModel}
          disableDensitySelector={false}
          isMobile={isMobile}
          onDensityChange={(densitySelection) => {
            setIsMobile(densitySelection === CustomDensityOption.Mobile);
          }}
          onCellClick={handleCellClick}
          apiRef={apiRef}
          serviceColumns={columns}
          setColumns={setColumns}
          filtersSearch={() => setFilterSearch(!filterSearch)}
          defaultReport={defaultReport}
          multiSearchQuery={multiSearchQuery}
        />
      </GridContainer>
      {imageURL && (
        <Modal
          open={showImage}
          onClose={handleImageClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <ImageWrapper container onMouseDown={handleImageClose}>
            <Image src={imageURL} />
          </ImageWrapper>
        </Modal>
      )}
    </>
  );
};
